import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Normalizes path for comparison by removing trailing slashes and converting to lower case.
 */
function normalizePath(p) {
  if (!p) return '';
  const clean = p.replace(/\/+$/, '').toLowerCase();
  return clean === '' ? '/' : clean;
}

/**
 * Creates a contextual prose linker for a specific page.
 * Keeps track of whether the company name ("MC Transport") has been linked to the frontpage,
 * and limits keyword links so that each target is only linked once per page.
 */
export function createProseLinker(currentPath = '') {
  const normCurrent = normalizePath(currentPath);
  const isFrontPage = normCurrent === '/';

  let hasLinkedCompany = isFrontPage; // on homepage, don't link company name to homepage
  const linkedTargets = new Set();
  if (normCurrent) {
    linkedTargets.add(normCurrent);
  }

  // Common keywords to internal page mapping
  // Ordered by specificity (longer/more specific phrases first)
  const KEYWORD_RULES = [
    { phrase: 'MC Autotransport', target: '/', isCompany: true },
    { phrase: 'MC Transport', target: '/', isCompany: true },
    { phrase: 'autohjælp 24/7', target: '/autohjaelp' },
    { phrase: 'døgnåbne vagtcentral', target: '/kontakt' },
    { phrase: 'døgnåben vagtcentral', target: '/kontakt' },
    { phrase: 'vores vagtcentral', target: '/kontakt' },
    { phrase: 'vagtcentral', target: '/kontakt' },
    { phrase: 'bugsering af lastbil', target: '/bugsering-af-lastbil' },
    { phrase: 'bugsering af bil', target: '/bugsering-af-bil' },
    { phrase: 'gardintrailer med medbringertruck', target: '/ydelser/gardintrailer-medbringertruck' },
    { phrase: 'transport på sengetrailer', target: '/ydelser/transport-paa-sengetrailer' },
    { phrase: 'sengetrailer', target: '/ydelser/transport-paa-sengetrailer' },
    { phrase: 'krantransport', target: '/ydelser/krantransport' },
    { phrase: 'maskintransport', target: '/ydelser/maskintransport' },
    { phrase: 'godstransport', target: '/ydelser/godstransport' },
    { phrase: 'servicevogn', target: '/ydelser/servicevogn' },
    { phrase: 'dækcenter til lastbil', target: '/daekcenter-lastbil' },
    { phrase: 'dækskift til lastbil', target: '/daekskift-lastbil' },
    { phrase: 'dækskift', target: '/daekskift-lastbil' },
    { phrase: 'dækskifte', target: '/daekskift-lastbil' },
    { phrase: 'dækcenter', target: '/daekcenter' },
    { phrase: 'fritrækning', target: '/fritraekning' },
    { phrase: 'bjærgningskøretøj', target: '/bjaergning' },
    { phrase: 'bjærgning', target: '/bjaergning' },
    { phrase: 'autotransport', target: '/ydelser/autotransport-ydelser' },
    { phrase: 'skiltevogn', target: '/skiltevogn' },
    { phrase: 'TMA køretøj', target: '/TMA' },
    { phrase: 'TMA kørsel', target: '/TMA' },
    { phrase: 'tow truck', target: '/tow-truck' },
    { phrase: 'autohjælp', target: '/autohjaelp' },
    { phrase: 'vejhjælp', target: '/autohjaelp' },
    { phrase: 'plads i Ugerløse', target: '/om-os' },
    { phrase: 'sikret plads', target: '/om-os' },
    { phrase: 'bliv chauffør', target: '/chauffoer-soeges' },
  ];

  return {
    get hasLinkedCompany() {
      return hasLinkedCompany;
    },

    format(text, options = {}) {
      if (!text || typeof text !== 'string') return text;

      // Tokenize and match phrases
      // We will perform matches while preventing nested replacements
      const segments = [{ text, isLink: false }];

      // 1. First priority: Check company name if not yet linked
      if (!hasLinkedCompany) {
        for (let i = 0; i < segments.length; i++) {
          if (segments[i].isLink) continue;
          const str = segments[i].text;
          const matchIndex = str.search(/\bMC\s+Transport\b|\bMC\s+Autotransport\b/i);
          if (matchIndex !== -1) {
            const matchedText = str.match(/\bMC\s+Transport\b|\bMC\s+Autotransport\b/i)[0];
            const before = str.slice(0, matchIndex);
            const after = str.slice(matchIndex + matchedText.length);

            const newSegments = [];
            if (before) newSegments.push({ text: before, isLink: false });
            newSegments.push({
              text: matchedText,
              isLink: true,
              target: '/',
              isCompany: true
            });
            if (after) newSegments.push({ text: after, isLink: false });

            segments.splice(i, 1, ...newSegments);
            hasLinkedCompany = true;
            linkedTargets.add('/');
            break; // only link company once per page!
          }
        }
      }

      // 2. Next: Other relevant internal keywords
      for (const rule of KEYWORD_RULES) {
        if (rule.isCompany) continue; // company already handled above
        const normTarget = normalizePath(rule.target);
        if (linkedTargets.has(normTarget)) continue; // already linked this target once on this page

        // Search through plain text segments
        for (let i = 0; i < segments.length; i++) {
          if (segments[i].isLink) continue;
          const str = segments[i].text;
          const escaped = rule.phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const regex = new RegExp(`\\b${escaped}\\b`, 'i');
          const matchIndex = str.search(regex);

          if (matchIndex !== -1) {
            const matchedText = str.match(regex)[0];
            const before = str.slice(0, matchIndex);
            const after = str.slice(matchIndex + matchedText.length);

            const newSegments = [];
            if (before) newSegments.push({ text: before, isLink: false });
            newSegments.push({
              text: matchedText,
              isLink: true,
              target: rule.target,
              isCompany: false
            });
            if (after) newSegments.push({ text: after, isLink: false });

            segments.splice(i, 1, ...newSegments);
            linkedTargets.add(normTarget);
            break; // move to next keyword rule
          }
        }
      }

      // 3. Convert segments to React nodes
      return segments.map((seg, idx) => {
        if (!seg.isLink) return seg.text;

        if (seg.isCompany) {
          return (
            <Link
              key={idx}
              to={seg.target}
              title="MC Transport Forside"
              className={options.companyLinkClass || "font-semibold text-zinc-950 hover:text-red-600 underline decoration-red-600/40 underline-offset-2 transition-colors"}
            >
              {seg.text}
            </Link>
          );
        }

        return (
          <Link
            key={idx}
            to={seg.target}
            className={options.linkClass || "text-red-600 font-medium hover:text-red-700 underline decoration-red-600/30 underline-offset-2 hover:decoration-red-600 transition-colors"}
          >
            {seg.text}
          </Link>
        );
      });
    },

    /**
     * Helper to process HTML strings (e.g. from CMS fields)
     * Replaces the first company occurrence with a link to / and relevant keywords with links.
     */
    formatHtml(htmlString) {
      if (!htmlString || typeof htmlString !== 'string') return htmlString;

      let result = htmlString;

      // 1. Link company first time if not linked yet
      if (!hasLinkedCompany) {
        const companyRegex = /(?<![<a\s/][^>]*)\b(MC\s+Transport|MC\s+Autotransport)\b(?![^<]*<\/a>)/i;
        if (companyRegex.test(result)) {
          result = result.replace(companyRegex, (m) => {
            hasLinkedCompany = true;
            linkedTargets.add('/');
            return `<a href="/" class="font-semibold text-zinc-950 hover:text-red-600 underline decoration-red-600/40 underline-offset-2 transition-colors" title="MC Transport Forside">${m}</a>`;
          });
        }
      }

      // 2. Link relevant keywords in HTML
      for (const rule of KEYWORD_RULES) {
        if (rule.isCompany) continue;
        const normTarget = normalizePath(rule.target);
        if (linkedTargets.has(normTarget)) continue;

        const escaped = rule.phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Match only outside existing anchor tags
        const regex = new RegExp(`(?<![<a\\s/][^>]*)\\b(${escaped})\\b(?![^<]*<\\/a>)`, 'i');
        if (regex.test(result)) {
          result = result.replace(regex, (m) => {
            linkedTargets.add(normTarget);
            return `<a href="${rule.target}" class="text-red-600 font-medium hover:text-red-700 underline decoration-red-600/30 underline-offset-2 hover:decoration-red-600 transition-colors">${m}</a>`;
          });
        }
      }

      return result;
    }
  };
}
